/**
 * Dev-детектор «потерянного промиса» (IMGM-1561).
 *
 * Кнопка кита запирается, пока жив промис её обработчика. Обработчик, который запустил запрос, но
 * промис не вернул (`() => { dispatch(save()) }`, `void handleSave()`, цепочка пропсов `() => void`),
 * оставляет кнопку открытой — двойной клик шлёт запрос дважды. Статикой (guard-тестом) такое видно
 * не всегда: промис теряется через несколько компонентов. Детектор ловит это в рантайме: middleware
 * стора считает стартовавшие thunk-и (`pending`), а кнопка сверяет счётчик до и после вызова
 * обработчика.
 *
 * Модуль — в `shared`, а не в `store`: кит (`ud-ui`) импортирует `shared`, но стора не знает.
 * В production проверка выключена: `callDetectingLostPromise` просто зовёт обработчик. Счётчик middleware
 * растёт и там (middleware подключён всегда), но его никто не читает.
 */

let startedRequests = 0;

const isDev = process.env.NODE_ENV !== 'production';

/** Зовёт middleware стора на каждый `pending` thunk-а (мутации и запросы RTK Query — тоже thunk-и). */
export function noteRequestStarted(): void {
  startedRequests += 1;
}

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  typeof (value as PromiseLike<unknown> | undefined)?.then === 'function';

/**
 * Вызывает обработчик действия и, если он запустил запрос, но вернул не промис, пишет
 * `console.error` с подписью кнопки. Результат обработчика возвращается как есть.
 */
export function callDetectingLostPromise<T>(handler: () => T, label: () => string): T {
  if (!isDev) return handler();

  const before = startedRequests;
  const result = handler();

  if (!isPromiseLike(result) && startedRequests > before) {
    console.error(
      `[lost-promise] «${label()}»: обработчик запустил запрос, но не вернул его промис — кнопка не заперта до ответа, ` +
        'двойной клик пошлёт запрос дважды. Верните промис из onClick / onConfirm / onSubmit (.claude/docs/ud-ui-gotchas.md).',
    );
  }

  return result;
}
